import { Page, Text, View } from "@react-pdf/renderer";
import { Style } from "@react-pdf/types";
import { useCallback, useMemo } from "react";

import PdfDocumentHtml from "./components/pdf-document-html/pdf-document-html.component";
import type { PdfDocumentPage } from "./pdf-document-page.type";

const viewStyle: Style = {
  position: "absolute",
  bottom: 30,
  left: 45,
  right: 45,
  textAlign: "center",
  color: "#616161",
  fontSize: 12
};

const nonFitFooterStyle = {
  height: "175px",
  width: "100%"
};

const fitFooterStyle = {
  height: "10px",
  width: "100%"
};

const pageNumberStyle = { fontSize: 12 };

const emptyPageNumberRender = ({
  pageNumber,
  subPageNumber
}: {
  pageNumber: number;
  subPageNumber: number;
}) => {
  return <Text></Text>;
};

export default function PdfDocumentPageItem({
  dpi,
  header,
  footer,
  contentStyle,
  bodyStyle,
  bodyHtml,
  pdfNativeTablesEnabled,
  children,
  showPageNumber,
  pageNumberPrefix,
  pageNumberPostfix,
  fitFooter
}: PdfDocumentPage & { fitFooter?: boolean }) {
  const renderPageNumber = useCallback(
    ({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
      totalPages === 1
        ? null
        : `${pageNumberPrefix}Page ${pageNumber} of ${totalPages}${pageNumberPostfix}`,
    [pageNumberPostfix, pageNumberPrefix]
  );

  const pageStyle = useMemo(
    () => ({
      paddingTop: 40,
      paddingBottom: 60,
      paddingHorizontal: 50,
      ...bodyStyle
    }),
    [bodyStyle]
  );
  const renderFooter = useMemo(
    () =>
      Boolean(footer) ? (
        <>
          {!fitFooter ? (
            <View style={nonFitFooterStyle} render={emptyPageNumberRender} />
          ) : (
            <View style={fitFooterStyle} render={emptyPageNumberRender} />
          )}

          {footer}
        </>
      ) : null,
    [fitFooter, footer]
  );

  return (
    <Page dpi={dpi} size="A4" style={pageStyle}>
      {Boolean(header) ? header : null}
      {Boolean(children) && children}
      {Boolean(bodyHtml) && (
        <View style={contentStyle}>
          <PdfDocumentHtml
            bodyHtml={bodyHtml}
            pdfNativeTablesEnabled={pdfNativeTablesEnabled}
          />
        </View>
      )}
      {showPageNumber && (
        <View fixed style={viewStyle}>
          <Text style={pageNumberStyle} render={renderPageNumber}></Text>
        </View>
      )}

      {renderFooter}
    </Page>
  );
}
